import { maxDate, minDate, type IsoDate } from './dates';
import type {
  CalendarEvent,
  CalendarEventSeverity,
  PlannedOrder,
  PlannedOrderStatus,
  PlanningModel,
} from './types';

/**
 * Everything the planner wants ordered from one vendor on one day. This
 * is the unit the procurement calendar draws and the unit a "Create PO"
 * button turns into a draft — the vendor gets one PO with all the parts
 * that are due to be ordered that day.
 */
export interface VendorOrderGroup {
  /** `${date}:${vendorId ?? 'none'}` */
  key: string;
  /** The day to place it: the earliest ideal order date, never in the past. */
  date: IsoDate;
  vendorId: string | null;
  vendorName: string;
  orders: PlannedOrder[];
  /** Last safe day across the lines; after this at least one part is late. */
  latestDate: IsoDate | null;
  /** Typical arrival if placed on `date`, from the slowest line. */
  expectedArrival: IsoDate | null;
  totalCents: number;
  linesWithoutCost: number;
  /** The most urgent line's status. */
  status: PlannedOrderStatus;
  severity: CalendarEventSeverity;
  /** True when at least one line has no vendor, so no PO can be drafted. */
  unassignable: boolean;
}

const STATUS_RANK: Record<PlannedOrderStatus, number> = {
  late: 0,
  order_now: 1,
  order_soon: 2,
  unknown_lead_time: 3,
  ok: 4,
};

function worstStatus(a: PlannedOrderStatus, b: PlannedOrderStatus): PlannedOrderStatus {
  return STATUS_RANK[a] <= STATUS_RANK[b] ? a : b;
}

function severityFor(status: PlannedOrderStatus, date: IsoDate, today: IsoDate): CalendarEventSeverity {
  if (status === 'late') return 'critical';
  if (status === 'order_now' || date <= today) return 'warning';
  if (status === 'order_soon') return 'warning';
  return 'info';
}

/** The day a suggested order shows up on the calendar: its ideal date, clamped to today. */
export function orderCalendarDate(order: PlannedOrder, today: IsoDate): IsoDate {
  if (order.status === 'late' || order.status === 'order_now') return today;
  return maxDate(order.idealOrderDate ?? today, today);
}

/**
 * Bucket the planner's suggestions by order day and vendor, most urgent
 * first. Parts with no supplier land in a per-day "No supplier" group so
 * they still show on the calendar (and point at the data gap) instead of
 * silently disappearing.
 */
export function buildVendorOrderGroups(model: PlanningModel): VendorOrderGroup[] {
  const { today } = model;
  const groups = new Map<string, VendorOrderGroup>();

  for (const order of model.orders) {
    const date = orderCalendarDate(order, today);
    const vendorId = order.supplier?.vendorId ?? null;
    const key = `${date}:${vendorId ?? 'none'}`;
    const latest = order.latestOrderDate ? maxDate(order.latestOrderDate, today) : null;
    // A group due today gets placed today, so quote today's arrival; a
    // future group is assumed to go out on its ideal day.
    const arrival =
      date <= today ? (order.arrivalIfOrderedToday ?? order.expectedArrivalDate) : order.expectedArrivalDate;
    const existing = groups.get(key);
    if (existing) {
      existing.orders.push(order);
      existing.status = worstStatus(existing.status, order.status);
      if (latest) existing.latestDate = existing.latestDate ? minDate(existing.latestDate, latest) : latest;
      if (arrival) {
        existing.expectedArrival = existing.expectedArrival ? maxDate(existing.expectedArrival, arrival) : arrival;
      }
      if (order.totalCents == null) existing.linesWithoutCost += 1;
      else existing.totalCents += order.totalCents;
    } else {
      groups.set(key, {
        key,
        date,
        vendorId,
        vendorName: order.supplier?.vendorName ?? 'No supplier on file',
        orders: [order],
        latestDate: latest,
        expectedArrival: arrival,
        totalCents: order.totalCents ?? 0,
        linesWithoutCost: order.totalCents == null ? 1 : 0,
        status: order.status,
        severity: 'info',
        unassignable: vendorId == null,
      });
    }
  }

  const out = [...groups.values()];
  for (const group of out) {
    group.severity = severityFor(group.status, group.date, today);
    group.orders.sort(
      (a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
        (a.latestOrderDate ?? '').localeCompare(b.latestOrderDate ?? '') ||
        a.itemId.localeCompare(b.itemId),
    );
  }
  out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      Number(a.unassignable) - Number(b.unassignable) ||
      a.vendorName.localeCompare(b.vendorName),
  );
  return out;
}

export function groupsByDate(groups: VendorOrderGroup[]): Map<IsoDate, VendorOrderGroup[]> {
  const map = new Map<IsoDate, VendorOrderGroup[]>();
  for (const group of groups) {
    const list = map.get(group.date) ?? [];
    list.push(group);
    map.set(group.date, list);
  }
  return map;
}

/**
 * The engine's `order_ideal` events duplicate what the vendor groups
 * already say, and its "Late: order …" markers sit on the same day as the
 * late group; drop both so each suggestion is drawn exactly once.
 */
export function isDrawnAsGroup(event: CalendarEvent): boolean {
  if (event.kind === 'order_ideal') return true;
  return event.kind === 'order_latest' && event.id.startsWith('late:');
}

export interface ProcurementCalendarSummary {
  /** Vendor groups due today or already late. */
  placeNow: VendorOrderGroup[];
  placeNowCents: number;
  /** The next group after today, if any. */
  nextGroup: VendorOrderGroup | null;
  /** Earliest last-safe-day still ahead of us. */
  nextDeadline: { date: IsoDate; group: VendorOrderGroup } | null;
  nextArrival: CalendarEvent | null;
  nextRunout: CalendarEvent | null;
}

export function summarizeProcurement(model: PlanningModel, groups: VendorOrderGroup[]): ProcurementCalendarSummary {
  const { today } = model;
  const placeNow = groups.filter((g) => g.date <= today);
  const future = groups.filter((g) => g.date > today);
  let nextDeadline: ProcurementCalendarSummary['nextDeadline'] = null;
  for (const group of groups) {
    if (!group.latestDate || group.latestDate < today) continue;
    if (!nextDeadline || group.latestDate < nextDeadline.date) {
      nextDeadline = { date: group.latestDate, group };
    }
  }
  const upcoming = (kind: CalendarEvent['kind']) =>
    model.events
      .filter((e) => e.kind === kind && e.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date))[0] ?? null;
  return {
    placeNow,
    placeNowCents: placeNow.reduce((acc, g) => acc + g.totalCents, 0),
    nextGroup: future[0] ?? null,
    nextDeadline,
    nextArrival: upcoming('receipt_expected'),
    nextRunout: upcoming('runout'),
  };
}
